import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { BACKEND_URL } from '../config/api';

const RatingModal = ({ children, onRatingAdded, preselectedParkId = null, park = null, isOpen: externalIsOpen, onClose: externalOnClose, onAuthRequired }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Use external state if provided, otherwise use internal state
  const modalIsOpen = externalIsOpen !== undefined ? externalIsOpen : isOpen;
  const handleClose = () => {
    if (externalOnClose) {
      externalOnClose();
    } else {
      setIsOpen(false);
    }
  };
  const [isLoading, setIsLoading] = useState(false);
  const [parks, setParks] = useState([]);
  const [selectedPark, setSelectedPark] = useState(preselectedParkId || '');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [hoveredRating, setHoveredRating] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [ratedParkData, setRatedParkData] = useState(null); // Store rated park for passing to comments

  const { user, isAuthenticated } = useAuth();

  // Load parks when modal opens
  useEffect(() => {
    if (modalIsOpen && isAuthenticated) {
      loadParks();
    }
  }, [modalIsOpen, isAuthenticated]);

  useEffect(() => {
    if (preselectedParkId) {
      setSelectedPark(preselectedParkId);
      const park = parks.find(p => p.id === preselectedParkId);
      if (park) {
        setSearchQuery(park.name);
      }
    }
  }, [preselectedParkId, parks]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-testid="park-search-input"]') && 
          !event.target.closest('.park-dropdown')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadParks = async () => {
    try {
      console.log('🔄 Loading parks for rating modal...');
      const response = await axios.get(`${BACKEND_URL}/api/parks`);
      console.log('✅ Parks loaded:', response.data.length, 'parks');
      setParks(response.data);
    } catch (error) {
      console.error('❌ Error loading parks:', error);
      toast.error('Failed to load parks');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      toast.error('Please login to rate locations');
      return;
    }

    if (!selectedPark) {
      toast.error('Please select a park');
      return;
    }

    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    try {
      setIsLoading(true);
      
      await axios.post(`${BACKEND_URL}/api/parks/${selectedPark}/ratings`, {
        rating: rating,
        comment: comment.trim() || null
      });

      // Store rated park data for "Comment on this location" button
      const ratedPark = parks.find(p => p.id === selectedPark);
      setRatedParkData(ratedPark);

      // Show success modal with celebration
      setShowSuccessModal(true);
      setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 2500); // Slightly longer than location success
      
      // Reset form
      setSelectedPark(preselectedParkId || '');
      setRating(0);
      setComment('');
      
      // Callback to refresh data
      if (onRatingAdded) {
        onRatingAdded();
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      const message = error.response?.data?.detail || 'Failed to submit rating';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedPark(preselectedParkId || '');
    setRating(0);
    setComment('');
    setHoveredRating(0);
  };

  const renderStars = () => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <button
          key={i}
          type="button"
          className={`text-3xl transition-colors ${
            i <= (hoveredRating || rating)
              ? 'text-yellow-500'
              : 'text-gray-300'
          } hover:text-yellow-400`}
          onMouseEnter={() => setHoveredRating(i)}
          onMouseLeave={() => setHoveredRating(0)}
          onClick={() => setRating(i)}
          data-testid={`star-${i}`}
        >
          ★
        </button>
      );
    }
    return stars;
  };

  const getRatingText = (ratingValue) => {
    const texts = {
      1: 'Poor 😞',
      2: 'Fair 😐',
      3: 'Good 😊',
      4: 'Very Good 😍',
      5: 'Excellent 🤩'
    };
    return texts[ratingValue] || '';
  };

  const selectedParkData = parks.find(p => p.id === selectedPark);

  // Filter parks based on search query (name only)
  const filteredParks = parks.filter(park => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return park.name.toLowerCase().includes(query);
  });

  const handleParkSelect = (park) => {
    setSelectedPark(park.id);
    setSearchQuery(park.name);
    setShowDropdown(false);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    console.log('🔍 Search query changed:', value);
    console.log('📋 Total parks available:', parks.length);
    
    setSearchQuery(value);
    setShowDropdown(true);
    
    // Clear selection if search doesn't match current selection
    if (selectedParkData && !selectedParkData.name.toLowerCase().includes(value.toLowerCase())) {
      setSelectedPark('');
    }
    
    // Debug filtered results  
    const filtered = parks.filter(park => {
      if (!value.trim()) return true;
      const query = value.toLowerCase();
      return park.name.toLowerCase().includes(query);
    });
    console.log('🎯 Filtered results:', filtered.length, 'parks match');
  };

  if (!isAuthenticated) {
    return (
      <Dialog open={modalIsOpen} onOpenChange={handleClose}>
        {children && (
          <DialogTrigger asChild>
            {children}
          </DialogTrigger>
        )}
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Authentication Required</DialogTitle>
            <DialogDescription>
              Please login to rate Happy Paws locations and share your experiences with the community.
            </DialogDescription>
          </DialogHeader>
          <div className="text-center py-6">
            <p className="text-gray-600 mb-4">
              Join our community to rate locations and help other dog owners!
            </p>
            <Button
              onClick={() => {
                setIsOpen(false);
                if (onAuthRequired) {
                  onAuthRequired(); // Trigger redirect modal in parent
                }
              }}
            >
              Login to Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={modalIsOpen} onOpenChange={(open) => {
        if (!open) {
          resetForm();
          handleClose();
        }
      }}>
        {children && (
          <DialogTrigger asChild>
            {children}
          </DialogTrigger>
        )}
        <DialogContent className="sm:max-w-md">
          {/* Orange X Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-orange-500 hover:bg-orange-600 transition-colors z-50"
            aria-label="Close"
            type="button"
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <span>⭐</span>
              <span>Rate a Happy Paws Location</span>
            </DialogTitle>
            <DialogDescription>
              Share your experience to help other dog owners find great places
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Park Selection with Search */}
            <div className="space-y-2 relative">
              <Label htmlFor="park_search">Enter Park Name</Label>
              <div className="relative">
                <Input
                  id="park_search"
                  type="text"
                  placeholder="Type park name..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => setShowDropdown(true)}
                  disabled={!!preselectedParkId}
                  data-testid="park-search-input"
                  className="pr-8"
                />
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400">
                  🔍
                </div>
              </div>
              
              {/* Dropdown with filtered results */}
              {showDropdown && !preselectedParkId && filteredParks.length > 0 && (
                <div className="park-dropdown absolute z-50 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {filteredParks.map((park) => (
                    <div
                      key={park.id}
                      onClick={() => handleParkSelect(park)}
                      className="p-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{park.name}</div>
                        </div>
                        {park.rating > 0 && (
                          <Badge variant="secondary" className="text-xs ml-2">
                            ★ {park.rating.toFixed(1)}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {showDropdown && !preselectedParkId && searchQuery && filteredParks.length === 0 && (
                <div className="absolute z-50 w-full bg-white border border-gray-200 rounded-md shadow-lg p-3 text-center text-gray-500 text-sm">
                  No park names found matching "{searchQuery}"
                </div>
              )}
              
              {selectedParkData && (
                <Card className="mt-2">
                  <CardContent className="p-3">
                    <div className="space-y-1">
                      <h4 className="font-medium text-sm">{selectedParkData.name}</h4>
                      {selectedParkData.description && (
                        <p className="text-xs text-gray-600 line-clamp-2">
                          {selectedParkData.description}
                        </p>
                      )}
                      <div className="flex items-center space-x-2 text-xs text-gray-500">
                        {selectedParkData.rating > 0 && (
                          <span>★ {selectedParkData.rating.toFixed(1)} ({selectedParkData.rating_count} reviews)</span>
                        )}
                        {selectedParkData.address && (
                          <span>📍 {selectedParkData.address}</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Rating Stars */}
            <div className="space-y-2">
              <Label>Your Rating</Label>
              <div className="flex items-center space-x-2">
                <div className="flex space-x-1" data-testid="rating-stars">
                  {renderStars()}
                </div>
                {rating > 0 && (
                  <span className="text-sm font-medium text-gray-700 ml-3">
                    {getRatingText(rating)}
                  </span>
                )}
              </div>
              {rating === 0 && (
                <p className="text-xs text-gray-500">Click stars to rate from 1-5</p>
              )}
            </div>

            {/* Comment */}
            <div className="space-y-2">
              <Label htmlFor="comment">Comment (Optional)</Label>
              <Textarea
                id="comment"
                placeholder="Share your experience... What did you and your dog enjoy most about this park?"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                maxLength={500}
                data-testid="rating-comment"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>Help other dog owners with specific details</span>
                <span>{comment.length}/500</span>
              </div>
            </div>

            {/* Submit Section */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="text-sm text-gray-600">
                Rating as: <span className="font-medium">{user?.username}</span>
              </div>
              <div className="flex space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!selectedPark || rating === 0 || isLoading}
                  data-testid="submit-rating-button"
                >
                  {isLoading ? 'Submitting...' : 'Submit Rating'}
                </Button>
              </div>
            </div>
          </form>

          {/* User verification notice */}
          {user && !user.is_verified && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 text-sm">
                📧 <strong>Note:</strong> Verify your email to ensure your ratings are trusted by the community.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rating Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={(open) => {
        if (!open) {
          setShowSuccessModal(false);
          handleClose(); // Close main rating modal
        }
      }}>
        <DialogContent 
          className="sm:max-w-md relative overflow-hidden" 
          style={{ 
            position: 'fixed', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)', 
            zIndex: 9999,
            maxHeight: '90vh'
          }}
        >
          {/* Standardized Close Button */}
          <button
            onClick={() => {
              setShowSuccessModal(false);
              handleClose();
            }}
            className="absolute top-3 right-3 z-50 w-7 h-7 rounded-full flex items-center justify-center text-white font-bold cursor-pointer transition-all duration-200 hover:scale-110"
            style={{
              background: 'linear-gradient(135deg, #8B4513, #A0522D)',
              boxShadow: '0 3px 10px rgba(139, 69, 19, 0.4)',
            }}
            onMouseOver={(e) => e.currentTarget.style.background = 'linear-gradient(135deg, #A0522D, #8B4513)'}
            onMouseOut={(e) => e.currentTarget.style.background = 'linear-gradient(135deg, #8B4513, #A0522D)'}
          >
            ×
          </button>
          
          <div className="text-center py-6">
            <div className="text-6xl mb-4">⭐</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
            <p className="text-gray-600 mb-6">Your rating has been submitted successfully!</p>
            
            {/* Rating Success Celebration - Different from location success */}
            {showCelebration && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="animate-pulse text-4xl">⭐</div>
                <div className="absolute top-4 left-4 animate-bounce text-2xl">🌟</div>
                <div className="absolute top-4 right-4 animate-spin text-2xl">✨</div>
                <div className="absolute bottom-4 left-6 animate-pulse text-xl" style={{animationDelay: '0.3s'}}>🏆</div>
                <div className="absolute bottom-4 right-6 animate-bounce text-xl" style={{animationDelay: '0.6s'}}>🎖️</div>
                <div className="absolute top-1/2 left-2 animate-spin text-lg" style={{animationDelay: '0.9s'}}>💫</div>
                <div className="absolute top-1/2 right-2 animate-pulse text-lg" style={{animationDelay: '1.2s'}}>🌠</div>
              </div>
            )}
            
            <div className="space-y-3">
              <Button
                onClick={() => window.location.href = '/map'}
                className="w-full bg-emerald-500 hover:bg-emerald-600"
              >
                🗺️ View More Parks
              </Button>
              <Button
                onClick={() => {
                  // Store park info in sessionStorage to open comments modal on map page
                  if (ratedParkData) {
                    sessionStorage.setItem('openCommentsForPark', JSON.stringify({
                      id: ratedParkData.id,
                      name: ratedParkData.name
                    }));
                  }
                  setShowSuccessModal(false);
                  handleClose();
                  window.location.href = '/map';
                }}
                className="w-full bg-blue-50 hover:bg-blue-100 border-blue-300"
                variant="outline"
              >
                💬 Comment on This Location
              </Button>
              <Button
                onClick={() => window.location.href = '/dashboard'}
                className="w-full"
                variant="outline"
              >
                📊 Back to Dashboard
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RatingModal;